import { useState } from "react";
import { Send, Layers, Mic, Check, Cloud, Zap, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { MicButton } from "@/components/MicButton";

const MOOD_EMOJIS = [
  { emoji: "😊", label: "Happy", score: 8 },
  { emoji: "😌", label: "Calm", score: 7 },
  { emoji: "😐", label: "Neutral", score: 5 },
  { emoji: "😔", label: "Sad", score: 3 },
  { emoji: "😢", label: "Struggling", score: 1 },
];

export function ChatInputArea({
  t, module, messages, sending, bulkMode, setBulkMode, bulkProgress,
  input, setInput, inputRef, saveDraft,
  sendMessage, sendBulkMessages, handleWakeWord,
  // Voice
  ttsEnabled, toggleTts, speaking, voice, currentVoiceInfo,
  setShowVoiceSelector, handleVoiceTranscript,
  // Question responder
  lastAiMsg, activeConv, handleQuestionResponderSubmit,
  // Mood
  moodNoteFor, moodNoteInput, setMoodNoteInput, quickMoodLog, submitMoodWithNote, skipMoodNote,
  // Draft status
  draftSaveStatus,
  // Response mode
  responseMode, setResponseMode,
}) {
  return (
    <div className="border-t border-border/50 bg-card/30 px-2 sm:px-4 py-1.5 sm:py-2 md:px-8">
      {/* Response Mode Toggle — always visible */}
      <div className="flex items-center justify-center mb-1.5 max-w-3xl mx-auto">
        <div data-testid="response-mode-toggle" className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-muted/40 border border-border/30">
          <button
            type="button"
            data-testid="mode-concise"
            onClick={() => setResponseMode("concise")}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-all",
              responseMode === "concise"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Zap className="w-3 h-3" strokeWidth={2} />
            Concise
          </button>
          <button
            type="button"
            data-testid="mode-comprehensive"
            onClick={() => setResponseMode("comprehensive")}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-all",
              responseMode === "comprehensive"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <BookOpen className="w-3 h-3" strokeWidth={2} />
            Comprehensive
          </button>
        </div>
      </div>
      {/* Bulk Progress */}
      {bulkMode && bulkProgress && (
        <div className="mb-1.5 max-w-3xl mx-auto">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(bulkProgress.completed / bulkProgress.total) * 100}%` }} />
            </div>
            <span>{bulkProgress.completed}/{bulkProgress.total}</span>
          </div>
        </div>
      )}

      {/* Input Form — clean single row */}
      <form
        data-testid="chat-form"
        onSubmit={e => { e.preventDefault(); bulkMode ? sendBulkMessages() : sendMessage(); }}
        className="flex items-end gap-1.5 max-w-3xl mx-auto"
      >
        {/* Bulk mode toggle */}
        <button
          type="button"
          data-testid="bulk-mode-toggle"
          onClick={() => setBulkMode(!bulkMode)}
          className={cn("p-2 rounded-xl transition-all flex-shrink-0 border", bulkMode ? "bg-primary/15 text-primary border-primary/30" : "text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/30 border-transparent")}
          title={bulkMode ? "Switch to normal mode" : "Multi-question mode"}
        >
          <Layers className="w-4 h-4" strokeWidth={bulkMode ? 2 : 1.5} />
        </button>

        {/* Mic button */}
        <MicButton
          onTranscript={handleVoiceTranscript}
          onWakeWord={handleWakeWord}
          size="sm"
          className="flex-shrink-0"
        />

        {/* Text input */}
        <div className="flex-1 relative min-w-0">
          <textarea
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={e => { setInput(e.target.value); }}
            onBlur={saveDraft}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey && !bulkMode) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={bulkMode ? "One question per line..." : messages.length > 0 ? "Type a message..." : t("chat.placeholder")}
            className="w-full resize-none rounded-2xl border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:border-primary/40 transition-colors min-h-[40px] max-h-[100px]"
            rows={bulkMode ? 3 : 1}
            disabled={sending}
          />
        </div>

        {/* Send button */}
        <button
          type="submit"
          data-testid="send-btn"
          disabled={sending || (!input.trim() && !bulkMode)}
          className={cn(
            "p-2.5 rounded-xl transition-all flex-shrink-0",
            input.trim() ? "bg-primary text-primary-foreground hover:opacity-90 shadow-sm" : "bg-muted text-muted-foreground"
          )}
        >
          {sending ? (
            <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
          ) : (
            <Send className="w-4 h-4" strokeWidth={2} />
          )}
        </button>
      </form>

      {/* Auto-save indicator — subtle but visible */}
      {draftSaveStatus === "saved" && input.trim() ? (
        <div data-testid="autosave-indicator" className="flex items-center gap-1 mt-1 ml-12 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium animate-in fade-in duration-200">
          <Check className="w-3 h-3" />
          <span>Draft saved</span>
        </div>
      ) : null}
    </div>
  );
}
